import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { OutputTypeEnum } from '@seeqdev/api/model/InputFeatureV1';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { TemplateInput } from '@/tools/models/template.store';
import type { DerivedFeatureV1 } from '@seeqdev/api/model/DerivedFeatureV1';
import type { FeatureRuleV1 } from '@seeqdev/api/model/FeatureRuleV1';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';
import type { TemplateRuleInputV1Object } from '@seeqdev/api/model/TemplateRuleInputV1Object';
import type { TemplateVersionOutputV1 } from '@seeqdev/api/model/TemplateVersionOutputV1';

/** Available template types used throughout template tools and scaling tables */
export const TEMPLATE_TYPES = {
  TOOL: 'TOOL',
  SCALING: 'SCALING',
} as const;

/** Instance name input feature required by backend for all templates */
export const INSTANCE_NAME_INPUT_FEATURE = {
  name: SeeqNames.Models.InstanceNameInputFeatureName,
  description: 'Name of the Template Instance',
  outputType: OutputTypeEnum.TEXT,
} as const;

/** Tailwind classes for output section borders in template tool forms */
export const OUTPUT_SECTION_BORDER_STYLES =
  'tw:mb-[15px] tw:w-full tw:items-center tw:border-b tw:border-l tw:border-r tw:border-solid tw:border-gray-300 tw:dark:border-gray-600 tw:rounded';

/** Version and draft metadata enriched onto template list items for Enterprise users with Locking. */
export type TemplateVersionMetadata = {
  /** Whether a draft exists for the current user */
  hasDraft: boolean;
  /** The versionId of the current user's draft, if one exists */
  draftVersionId?: string;
  /** The production version number (e.g. 1, 2, 3) or undefined if no versions exist */
  productionVersionNumber?: number;
  /** Total number of published versions */
  versionCount: number;
  /** Whether the production version is locked (true if any versions exist, since publishing locks the draft) */
  isLocked: boolean;
  /** Published versions, sorted newest first */
  versions: TemplateVersionOutputV1[];
};

/** Union type of supported template type values from `TEMPLATE_TYPES`. */
export type TemplateType = (typeof TEMPLATE_TYPES)[keyof typeof TEMPLATE_TYPES];

/** Shape for identifying a template input by any of its known ID fields. */
export type TemplateInputIds = { inputFeatureId?: string; referenceId?: string; id?: string };

/**
 * Wire-format metadata captured from a template output's source item, mirroring the matching fields on
 * `DerivedFeatureV1`. Mixed into `NewlyAddedDerivedFeature` so the metadata flows from
 * confirm-time capture to save without re-declaring the fields.
 */
export type OutputItemMetadataFields = {
  /** Wire-format item type (e.g. "Signal", "Scalar", "Condition"). */
  outputItemType?: string;
  /** Wire-format metadata map (e.g. `{ [ValueUom]: "degF" }`). */
  outputItemMetadata?: Record<string, string>;
};

/** Value object from the output select UI for adding outputs to a template. */
export type TemplateOutputSelectValue = {
  id: string;
  name: string;
};

/** Formula parameter entry returned by the formulas API. */
type FormulaParameter = {
  name?: string;
  item?: {
    id?: string;
  };
};

/** Subset of formula metadata used to build formula creator rules for derived features. */
export type FormulaOutput = {
  name?: string;
  scopedTo?: string;
  formula?: string;
  parameters?: FormulaParameter[];
};

/** Dependency item returned from `getDependencies` for template outputs.
 * Used to build tool inputs and track parent relationships for dependency filtering.
 */
export type OutputDependencyItem = {
  id: string;
  name: string;
  type: string;
  ancestors?: Array<{ name?: string }>;
  parameterOf?: Array<{ id: string }>;
};

/**
 * Represents a template input collected from formula dependencies.
 */
export interface CollectedInput {
  /** Unique identifier for the input item */
  id: string;
  /** Current name of the input */
  name: string;
  /** Original name before any transformations */
  originalName: string;
  /** Seeq item type (e.g., StoredSignal, CalculatedScalar) */
  inputType: string;
  /** Description text for the input */
  description: string;
  /** Name of the parent asset if this input belongs to an asset */
  parent?: string;
}

/**
 * Result of collecting inputs and their parent IDs from formula dependencies.
 */
export interface CollectedInputsAndParents {
  /** List of collected input items */
  inputs: CollectedInput[];
  /** Set of IDs that are parents (intermediates) in the dependency tree */
  parentIds: Set<string>;
}

/** Newly added derived feature created in the UI (unsaved until template save). */
export type NewlyAddedDerivedFeature = {
  id?: string;
  /** Provided if the feature is an intermediate feature */
  referenceId?: string;
  isOutput: boolean;
  name: string;
  rules?: FeatureRuleV1[];
  selectedItem?: { name: string; id: string };
  /** True if this intermediate is being treated as an input */
  intermediateAsInput?: boolean;
  /** True if this was originally an existing intermediate */
  wasExistingIntermediate?: boolean;
  /** Preserve original feature for restore */
  originalFeature?: DerivedFeatureV1;
  /** Existing intermediates pruned by filterUnusedFeatures when this feature was toggled to input.
   * Restored alongside this feature when it is toggled back to intermediate. */
  suspendedExistingDeps?: TemplateDerivedFeature[];
  /** True if this intermediate is mapped to an existing intermediate */
  isMapped?: boolean;
  /** ID of the existing intermediate this feature is mapped to */
  mappedFeatureId?: string;
} & OutputItemMetadataFields;

/**
 * Output that can be added to the template. Requires id for dependency resolution.
 */
export type SelectableOutput = NewlyAddedDerivedFeature & { id: string };

/** Pairs a derived feature with its original index before sorting, used for dependency-based reordering. */
export type IndexedFeature = {
  feature: NewlyAddedDerivedFeature;
  oldIndex: number;
};

/** Newly added template input created in the UI (unsaved until template save). */
export type NewlyAddedInputFeature = TemplateInput & {
  referenceId: string;
  id: string;
};

/**
 * Extended derived feature type used in template tools to include:
 * - selectedItem: Selected item info for edited formula inputs
 * - columnDefinition: Links this derived feature to a table column ID when the template has been applied to a scaling
 * table. Used to display the actual table column name rather than the original feature name.
 */
export type TemplateDerivedFeature = DerivedFeatureV1 & {
  selectedItem?: { name: string; id: string };
  columnDefinition?: { id: string };
};

/**
 * Internal type that extends TemplateRuleInputV1Object with a featureId field for reindexing.
 * The featureId stores the id of the referenced feature, allowing reliable
 * matching after features are reordered by sorting/filtering. The featureId is stripped
 * before the final save to the backend.
 */
export type TemplateRuleInputWithFeatureId = TemplateRuleInputV1Object & {
  featureId?: string;
};

/** Extended derived feature type used in template tools that includes an optional column definition id for derived
 * features to map column names correctly.
 */
export type DerivedFeatureWithColumnDefinition = DerivedFeatureV1 & {
  columnDefinition?: Pick<ColumnDefinition, 'id'>;
};

/** Extended template item for shared templates that include version data and permission information that are used to
 *  determine a users access and ownership of a template.
 */
export interface EnrichedSharedTemplateItem extends ItemSearchPreviewV1 {
  /** Version and draft metadata, present when Locking is enabled */
  versionMetadata?: TemplateVersionMetadata;
  /** Whether the current user has write permission on this template */
  canWrite?: boolean;
}
